/** Palette UNICA della pagina ordini: i toni dei badge di stato. Vive in un
 *  modulo suo perche' la usano anche i filtri, la colonna Spedizione e lo
 *  storico del tracking (Davide 2026-09-23: «tutti i colori nello stile delle
 *  etichette di stato»), e dentro OrderCard avrebbe creato import circolari.
 *  Regola d'uso fuori dai badge: PIENO = e' cosi' / attivo, CONTORNO = spento
 *  o in attesa. Stessa forma, stesso colore. */

/** Un colore per OGNI badge (Davide 2026-09-01: «tutti di colori differenti»).
 *
 *  Prima i toni erano sei, semantici, e piu' badge se li dividevano: quattro
 *  cose diverse in verde, quattro in rosso. A colpo d'occhio due badge dello
 *  stesso colore si leggono come la stessa cosa, e in una colonna dove stanno
 *  affiancati e' proprio quello che non deve succedere.
 *
 *  Restano **famiglie**: rossi/rosa/arancio = costa soldi o va fatto, verdi/
 *  teal = fatto, blu = informativo, viola/indaco = ritiro e sostituzioni,
 *  grigi = neutro. Il colpo d'occhio resta, ma dentro la famiglia ogni badge
 *  ha il suo.
 *
 *  Palette **Google/Material**, livelli 700-900: gli unici che reggono il testo
 *  bianco a 9.5px, dove vale la soglia WCAG piena (4.5:1) e non il 3:1 dei
 *  testi grandi. Misurati, non scelti a occhio — ``badgeToni.test.ts`` verifica
 *  contrasto **e unicita'**: due badge non possono tornare dello stesso colore
 *  per distrazione. Bocciati dalla misura: Yellow 900 `#a17b00` (3.93) e
 *  DeepOrange 800 `#d84315` (4.44). */
export type StatoTone =
  | "nuovo"
  | "in-corso"
  | "pronto"
  | "spedito"
  | "annullato"
  | "pagato"
  | "non-ordinato"
  | "da-incassare"
  | "da-pagare"
  | "da-pagare-atteso"
  | "da-ordinare"
  | "ordinato"
  | "pronto-ritiro"
  | "gia-spedita"
  | "ldv-inviata"
  | "ldv-mancante"
  | "in-casa"
  | "doppio"
  | "sost-fornitore"
  | "sost-as"
  | "bonifico"
  | "consegnata"
  | "ritorno-mittente"
  | "scaduta";

export const BADGE_TONI: Record<StatoTone, string> = {
  // stato dell'ordine
  nuovo: "#1a73e8", // Blue 600
  "in-corso": "#c05600", // Orange 800
  pronto: "#188038", // Green 700
  spedito: "#1967d2", // Blue 800
  annullato: "#5f6368", // Grey 700
  // soldi
  pagato: "#137333", // Green 800
  "non-ordinato": "#455a64", // Blue Grey 700
  "da-incassare": "#ad1457", // Pink 800
  "da-pagare": "#c5221f", // Red 700 — dai 2 giorni in su
  "da-pagare-atteso": "#b06000", // Orange 900 — appena ordinato
  bonifico: "#8f6100", // Yellow 900
  // flusso col fornitore
  "da-ordinare": "#5d4037", // Brown 700
  ordinato: "#00695c", // Teal 800
  // ritiro e LDV
  "pronto-ritiro": "#8430ce", // Purple 600
  "gia-spedita": "#01579b", // Light Blue 900
  "ldv-inviata": "#004d40", // Teal 900
  "ldv-mancante": "#bf360c", // Deep Orange 900
  // contorno
  "in-casa": "#2e7d32", // Green 600 scuro
  doppio: "#880e4f", // Pink 900
  "sost-fornitore": "#303f9f", // Indigo 700
  "sost-as": "#512da8", // Deep Purple 700
  // corriere e scadenza (2026-09-23: i colori della pagina ordini sono TUTTI
  // questi). «In viaggio» usa il blu di «spedito»: e' la stessa cosa vista
  // dal corriere.
  consegnata: "#1b5e20", // Green 900
  "ritorno-mittente": "#c62828", // Red 800 — il pacco torna indietro
  scaduta: "#b71c1c", // Red 900
};

/** La forma di un badge di stato (quella di `StatoBadge`): chi la riusa —
 *  scadenza, stato del corriere, «Sost.» — si legge come un'etichetta di
 *  stato. Il colore arriva a parte, da `pieno` o `contorno`. */
export const FORMA_BADGE =
  // Redesign 2026-10-27: minuscolo a 11px in grassetto (era maiuscoletto da
  // 9,5px). Sul telefono l'etichetta normale c'era gia' ed e' piaciuta; il
  // maiuscolo piccolo e' la forma piu' faticosa da leggere.
  "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md border-[1.5px] px-1.5 py-0.5 text-center text-[11px] font-semibold leading-tight shrink-0";

type Colori = { backgroundColor: string; borderColor: string; color: string };
const tinta = (fondo: string, testo = "#ffffff"): Colori => ({
  backgroundColor: fondo,
  borderColor: fondo,
  color: testo,
});

/** Chip del CORRIERE (Davide 2026-09-28): SDA azzurro, BRT rosso, Poste
 *  giallo. Fuori da ``BADGE_TONI``: e' un marchio, non uno stato. Il giallo
 *  Poste regge solo testo scuro. Ignoto → ``null``, resta il chip neutro. */
export function coloriCorriere(corriere: string): Colori | null {
  const c = corriere.trim().toUpperCase();
  if (c.startsWith("SDA")) return tinta("#0277bd"); // Light Blue 800
  if (c.startsWith("BRT")) return tinta("#c8102e"); // rosso BRT
  if (c.startsWith("POSTE")) return tinta("#fdd835", "#202124"); // Yellow 600
  return null;
}

/** Chip del PAESE di spedizione, stessa forma del corriere: un colore per
 *  paese, preso dalla bandiera e scurito finche' regge il testo bianco. */
const TONI_PAESE: Record<string, string> = {
  IT: "#188038", // verde
  FR: "#1a3d8f", // blu
  ES: "#8f6100", // oro
  DE: "#3c4043", // nero
  BE: "#5d4037", // bruno
  NL: "#c05600", // arancio
  AT: "#880e4f", // rosso-bordeaux
  PT: "#00695c", // verde-teal
  CH: "#ad1457", // rosa
  LU: "#01579b", // azzurro scuro
  GB: "#303f9f", // indaco
};

export function coloriPaese(iso: string): Colori {
  return tinta(TONI_PAESE[iso.trim().toUpperCase()] ?? "#5f6368"); // Grey 700
}

/** PIENO: «e' cosi'». Testo bianco sul tono (contrasto verificato nei test). */
export function pieno(tono: StatoTone): {
  backgroundColor: string;
  borderColor: string;
  color: string;
} {
  const colore = BADGE_TONI[tono];
  return { backgroundColor: colore, borderColor: colore, color: "#ffffff" };
}

/** CONTORNO: «spento» o «in attesa». Testo nel tono su bianco — stesso
 *  contrasto del pieno, a colori invertiti; il bordo e' il tono schiarito. */
export function contorno(tono: StatoTone): {
  backgroundColor: string;
  borderColor: string;
  color: string;
} {
  const colore = BADGE_TONI[tono];
  // Fondo e testo seguono il tema: di notte il fondo e' la card scura e il
  // tono si schiarisce verso il testo (`--om-mix`, 0% di giorno = invariato).
  return {
    backgroundColor: "rgb(var(--om-card))",
    borderColor: `color-mix(in srgb, ${colore} 45%, rgb(var(--om-card)))`,
    color: `color-mix(in srgb, ${colore}, rgb(var(--om-ink)) var(--om-mix))`,
  };
}
